import { ContentBlock } from 'draft-js'
import { IsEqualCustomizer, isEqualWith } from 'lodash'
import { compose, lifecycle, withHandlers, withState } from 'recompose'
import { Subscription } from 'rxjs'
import { ANS } from '~/reactapp/story/ans/types'
import {
  BlockRendererProps,
  WithAnsEnhancer,
  WithAnsState,
  WithBeforeUpdateProvided,
  WithEditingEnhancer,
  WithIsNewProvided,
  WithOnSetIsEditingProvided,
  WithSubmitHandler,
  WithSyncStateProvided,
} from './types'

export const getAnsFromBlock = (block: ContentBlock): ANS =>
  block.getData().get('ans')

export const withAnsEnhancer = withState<
  BlockRendererProps,
  WithAnsState,
  'ans',
  'setAns'
>('ans', 'setAns', ({ block }) => getAnsFromBlock(block))

type TSubmitHandlerExpectedProps = WithEditingEnhancer &
  WithAnsEnhancer &
  BlockRendererProps &
  WithBeforeUpdateProvided

type ANSMassager<TProps> = (ans: ANS, props: TProps, data: unknown) => ANS

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function submitHandler<TProps extends TSubmitHandlerExpectedProps>(
  massageANS: ANSMassager<TProps>,
  isEqualCustomizer?: IsEqualCustomizer
) {
  return {
    submitValues: (props: TProps) => (
      editingDone = true,
      skipSetIsEditing = false,
      data: unknown
    ) => {
      const {
        setIsEditing,
        updateAnsData,
        ans,
        setAns,
        block,
        beforeUpdate,
      } = props
      const newAns = massageANS(ans, props, data)

      // Optional beforeUpdate hook for validation and additional changes
      if (editingDone && beforeUpdate && beforeUpdate(newAns) === false) {
        // Stop submission if the hook return false literally
        return newAns
      }

      // To reduce unnecessary undo stack, only update if ANS has changed
      if (!isEqualWith(getAnsFromBlock(block), newAns, isEqualCustomizer)) {
        setAns(newAns)
        // If editing is not yet done, update ANS data as replacing mode since the initial ANS is in intermediate state
        updateAnsData(newAns, !editingDone)
      }

      setIsEditing && !skipSetIsEditing && setIsEditing(!editingDone)
    },
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
export const cancelHandler = (deriveStateFromAns?: (props: any) => void) => ({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cancel: (props: any) => () => {
    // Reset state
    if (deriveStateFromAns && props.syncState) {
      props.syncState(deriveStateFromAns(props))
    }

    props.setIsEditing(false)
  },
})

type LifecycleEnhancer = WithSubmitHandler & WithIsNewProvided
export const lifecycleEnhancer = lifecycle<LifecycleEnhancer, {}>({
  componentDidMount() {
    if (this.props.isNew) {
      this.props.submitValues(false)
    }
  },
})

// setIsEditing is exposed to require edititng mode
// components should not assume that they are in editing mode right after
// the call of `setIsEditing`. Instead, confirmation goes through compositeEditorSubject
// It set `isEditing` prop to true/false and optionally call onEditing, if provided

export const editingHandlersEnhancer = withHandlers<BlockRendererProps, {}>({
  setIsEditing: ({ block, blockProps }: BlockRendererProps) => (
    isEditing: boolean
  ) => {
    if (isEditing) {
      blockProps.onStartEdit(block.getKey())
    } else {
      blockProps.onFinishEdit(block.getKey())
    }
  },
  // Release editing revoke editing state for all registered components
  // This is useful when user start dragging a block while another block is in the editing state
  releaseEditing: ({ blockProps }: BlockRendererProps) => () => {
    blockProps.evictActiveInlineEditor && blockProps.evictActiveInlineEditor()
  },
  updateAnsData: ({ block, blockProps }: BlockRendererProps) => (
    ans: ANS,
    replace?: boolean
  ) => {
    blockProps.setBlockData(
      block.getKey(),
      block.getData().set('ans', ans),
      replace
    )
  },
})

// Editing lifecycle receive editing confirmations and set `isEditing` and call `onEditing`
// if provided.
// isEditing might be cleared not only as a result of `setIsEditing` call but also:
// - Editor is going to save story and set editing to false to flush any changes
// - Another block has been clicked so editing state should be revoked
// isEditing might be set to true for:
// - Tansa browse/replace integration, to show up content (additional enhancer will be used for tansa)
// - Find & Replace integration. Same as tansa.

type EditingLifecycleOuter = BlockRendererProps & WithOnSetIsEditingProvided
interface EditingLifecycleInstance {
  subscription?: Subscription
}

export const editingLifecycleEnhancer = lifecycle<
  EditingLifecycleOuter,
  {},
  EditingLifecycleInstance
>({
  componentDidMount() {
    if (
      !this.props ||
      !this.props.blockProps ||
      !this.props.blockProps.compositeEditorSubject
    ) {
      return
    }
    // Watch compositeEditorSubject for any changes, addressed to the current block
    // by passing only matching block keys.
    // Also check for isEditing to be set
    this.subscription = this.props.blockProps.compositeEditorSubject
      .filter(({ blockKey, isEditing }) => {
        return blockKey === this.props.block.getKey() && isEditing !== undefined
      })
      .subscribe({
        next: ({ isEditing }) => {
          this.props.onSetIsEditing && this.props.onSetIsEditing({ isEditing })
          this.setState({ isEditing })
        },
      })
  },
  componentWillUnmount() {
    this.subscription && this.subscription.unsubscribe()
  },
})

export const withEditingEnhancer = compose<
  WithEditingEnhancer,
  EditingLifecycleOuter
>(editingHandlersEnhancer, editingLifecycleEnhancer)

type SyncStateEnhancerOuter<TSyncState> = BlockRendererProps &
  WithAnsEnhancer &
  WithEditingEnhancer &
  WithSyncStateProvided<TSyncState>

interface SyncStateEnhancerInstance {
  lastBlock?: ContentBlock
}
// This is for handling state data sync for undo, redo, or any block ANS data changes in main eidtor
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getSyncStateEnhancer<TSyncState>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deriveStateFromProps?: (props: any) => TSyncState,
  isEqualCustomizer?: IsEqualCustomizer
) {
  return lifecycle<
    SyncStateEnhancerOuter<TSyncState>,
    {},
    SyncStateEnhancerInstance
  >({
    componentDidMount() {
      // Simple memoization for comparison
      this.lastBlock = this.props.block
    },
    componentDidUpdate() {
      if (!this.props.isEditing && this.lastBlock !== this.props.block) {
        this.lastBlock = this.props.block
        const ans = getAnsFromBlock(this.props.block)
        if (!isEqualWith(ans, this.props.ans, isEqualCustomizer)) {
          this.props.setAns(ans)
          if (deriveStateFromProps && this.props.syncState) {
            this.props.syncState(
              deriveStateFromProps({
                ...this.props,
                ans,
              })
            )
          }
        }
      }
    },
  })
}
